// SHARED (isomorphic): the app rendered identically on the server (via ServerAppTree) and the client
// (via ThemedAppRoot). Its output must match in both runtimes for hydration to succeed.
import { matchPath, Route, Routes, useLocation } from 'react-router';
import React, { Suspense, useContext } from 'react';
import { ThemeProvider } from '@naf/theme-provider';
import { ThemeProvider as SimbaThemeProvider } from '@naf-simba/theme';
import { ThemeProvider as EmotionThemeProvider } from '@emotion/react';
import { themeLight } from '@lib/nafTheme';
import { ToastProvider } from '@naf-simba/elements-toast';
import { ThemeContextValue } from '@lib/ThemeContext';
import { assertThemeComplete } from '@lib/assertThemeComplete';
import { useTheme } from '../../hooks/useTheme';
import useSelector from '../../redux/typedHooks';

// Styles
import { GlobalStyle } from '../../components/GlobalStyle';

// Components
import Footer from '../../components/footer/Footer';
import { CustomCookieModal } from '../../components/Cookies/CookieModal';
import SystemMessage from '../../components/SystemMessage';
import { ScrollToTop } from '../../components/ScrollToTop';
import { ThemeVariants } from '../../../../types/themes';
import NPS from '../../pages/NPS/NPS';

import { ContentSwitch } from '../../routers/ContentSwitch';
import ErrorBoundary from '../../components/error-component/ErrorBoundary';
import Navbar from '../../components/navbar/Navbar';
import * as S from './ThemedApp.styles';
import {
  ArticleTestPage,
  articleTestPagePath,
  BecomeAMember,
  becomeAMemberPath,
  BilGuideRouter,
  bilGuideRouterPath,
  buyIdpPath,
  BuyInternationalDrivingPermit,
  Chat,
  ConsumerTestRouter,
  CookieDeclaration,
  cookieDeclarationPath,
  CustomizeCookies,
  customizeCookiesPath,
  FrontPage,
  frontPagePath,
  IdpArticle,
  idpArticlePath,
  IdpReceipt,
  idpReceiptPath,
  InternalServerError,
  internalServerErrorPath,
  LoggedOut,
  loggedOutPath,
  LoggingIn,
  loggingInPath,
  MyProfileRouter,
  myProfileRouterPath,
  OrderReceipt,
  orderReceiptPath,
  QrLandingPage,
  qrLandingPagePath,
  Welcome,
  welcomePath,
  PaymentCancelled,
  paymentCancelledPath,
  loginPath,
  Login,
} from './ThemedApp.utils';

interface ThemedAppProps {
  initialTheme?: ThemeVariants | null;
  abTestWeight?: number;
}

export const ThemedApp = ({ initialTheme, abTestWeight }: ThemedAppProps) => {
  const location = useLocation();
  const theme = useContext(ThemeContextValue);
  useTheme(initialTheme);

  // Dev-only: fail loudly if the theme actually fed to the providers is missing values the
  // migrated (Emotion) base styles depend on, instead of silently rendering blank CSS (NOA-3189).
  assertThemeComplete(theme ?? themeLight, 'EmotionThemeProvider theme');

  const isNafLoginPage = location.pathname === loginPath;
  const simbaCdnRoot = useSelector((state) => state.application.simbaCdnRoot);
  const isMembershipFlowPage =
    matchPath({ path: becomeAMemberPath, end: true }, location.pathname) != null ||
    matchPath({ path: welcomePath, end: true }, location.pathname) != null;

  return (
    // Emotion reads its own theme context (styled-components reads @naf/theme-provider's).
    // Feed both the same NAF theme so migrated components' `props.theme` resolves (NOA-3189).
    // `theme` is undefined until useTheme's client effects run (SSR + first hydration render);
    // fall back to themeLight so Emotion's ThemeProvider always gets a valid object (it throws
    // on a non-object) and server/first-client render stay identical (no hydration mismatch).
    <EmotionThemeProvider theme={theme ?? themeLight}>
      <SimbaThemeProvider cdnRoot={simbaCdnRoot}>
        <ToastProvider>
          <ErrorBoundary>
            <SystemMessage />
            {!isNafLoginPage && <Navbar />}
          </ErrorBoundary>
          {/* Same fallback as Emotion's above: without it the styled-components provider merges
              `undefined` over the raw `@naf/theme` themeLight, and the global stylesheet paints
              links in the pre-Simba green until useTheme's effects run. */}
          <ThemeProvider theme={theme ?? themeLight}>
            <GlobalStyle />
            <CustomCookieModal />
            {!isNafLoginPage && (
              <Suspense fallback={null}>
                <Chat isEnabled={!isMembershipFlowPage} />
              </Suspense>
            )}
            <S.HeaderAndContentWrapper>
              <S.AppWrapper id="appWrapper">
                <S.ContentWrapper>
                  <ErrorBoundary>
                    <ScrollToTop>
                      <Routes location={location}>
                        <Route path={loginPath} element={<Login />} />
                        <Route path={loggingInPath} element={<LoggingIn />} />
                        <Route path={loggedOutPath} element={<LoggedOut />} />
                        <Route path={frontPagePath} element={<FrontPage abTestWeight={abTestWeight} />} />
                        <Route path="/nps" element={<NPS />} />
                        <Route path={articleTestPagePath} element={<ArticleTestPage />} />
                        <Route path={`${myProfileRouterPath}/*`} element={<MyProfileRouter />} />
                        <Route path={`${bilGuideRouterPath}/*`} element={<BilGuideRouter />} />
                        <Route path="/forbrukertester" element={<ContentSwitch />} />
                        <Route path="/forbrukertester/*" element={<ConsumerTestRouter />} />
                        <Route path={orderReceiptPath} element={<OrderReceipt />} />
                        <Route path={becomeAMemberPath} element={<BecomeAMember />} />
                        <Route path={welcomePath} element={<Welcome />} />
                        <Route path={paymentCancelledPath} element={<PaymentCancelled />} />
                        <Route path={buyIdpPath} element={<BuyInternationalDrivingPermit />} />
                        <Route path={idpReceiptPath} element={<IdpReceipt />} />
                        <Route path={cookieDeclarationPath} element={<CookieDeclaration />} />
                        <Route path={customizeCookiesPath} element={<CustomizeCookies />} />
                        <Route path={idpArticlePath} element={<IdpArticle />} />
                        <Route path={qrLandingPagePath} element={<QrLandingPage />} />
                        <Route path={internalServerErrorPath} element={<InternalServerError />} />
                        <Route path="*" element={<ContentSwitch />} />
                      </Routes>
                    </ScrollToTop>
                  </ErrorBoundary>
                </S.ContentWrapper>
                {!isNafLoginPage && <Footer />}
              </S.AppWrapper>
            </S.HeaderAndContentWrapper>
          </ThemeProvider>
        </ToastProvider>
      </SimbaThemeProvider>
    </EmotionThemeProvider>
  );
};
